// 📂 frontend/src/contexts/ThemeContext.tsx
import React, { createContext, useState, useEffect, ReactNode } from 'react';

type ThemeType = 'light' | 'dark' | 'futuristic';

interface ThemeColors {
  background: string;
  surface: string;
  primary: string;
  secondary: string;
  accent: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  danger: string;
}

interface ThemeConfig {
  colors: ThemeColors;
  borderRadius: {
    small: string;
    medium: string;
    large: string;
  };
  shadows: {
    small: string;
    medium: string;
    large: string;
  };
  transitions: {
    fast: string;
    normal: string;
    slow: string;
  };
}

interface ThemeContextType {
  currentTheme: ThemeType;
  theme: ThemeConfig;
  setTheme: (theme: ThemeType) => void;
}

const themes: Record<ThemeType, ThemeConfig> = {
  light: {
    colors: {
      background: '#f9fafb',
      surface: '#ffffff',
      primary: '#3b82f6',
      secondary: '#6366f1',
      accent: '#8b5cf6',
      text: '#111827',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444'
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '12px'
    },
    shadows: {
      small: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      large: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    },
    transitions: {
      fast: 'all 0.15s ease',
      normal: 'all 0.3s ease',
      slow: 'all 0.5s ease'
    }
  },
  dark: {
    colors: {
      background: '#0f172a',
      surface: '#1e293b',
      primary: '#3b82f6',
      secondary: '#6366f1',
      accent: '#8b5cf6',
      text: '#f1f5f9',
      textSecondary: '#94a3b8',
      border: '#334155',
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444'
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '12px'
    },
    shadows: {
      small: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
      medium: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
      large: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
    },
    transitions: {
      fast: 'all 0.15s ease',
      normal: 'all 0.3s ease',
      slow: 'all 0.5s ease'
    }
  },
  futuristic: {
    colors: {
      background: '#0a0e27',
      surface: '#151a3a',
      primary: '#00d9ff',
      secondary: '#ff006e',
      accent: '#ffb700',
      text: '#e0e6ff',
      textSecondary: '#a3b3ff',
      border: '#2a3155',
      success: '#00ff88',
      warning: '#ffb700',
      danger: '#ff006e'
    },
    borderRadius: {
      small: '2px',
      medium: '4px',
      large: '8px'
    },
    shadows: {
      small: '0 0 10px rgba(0, 217, 255, 0.2)',
      medium: '0 0 20px rgba(0, 217, 255, 0.3)',
      large: '0 0 30px rgba(0, 217, 255, 0.4)'
    },
    transitions: {
      fast: 'all 0.15s ease',
      normal: 'all 0.3s ease',
      slow: 'all 0.5s ease'
    }
  }
};

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('app-theme');
    return (savedTheme as ThemeType) || 'dark';
  });

  const setTheme = (theme: ThemeType) => {
    setCurrentTheme(theme);
    localStorage.setItem('app-theme', theme);
  };

  useEffect(() => {
    // Aplică tema la document root
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  const value: ThemeContextType = {
    currentTheme,
    theme: themes[currentTheme],
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};